import {
  Organization,
  LegalEntityType,
  ClientTier,
  OrganizationServiceStatus,
} from '../types/organization.type';
import {
  buildServiceStatusUpdateFields,
  normalizeOrganizationServiceStatus,
  resolveServiceStatus,
} from './organizationServiceStatus.utils';

export const hasOrganizationId = (value: unknown): value is Organization => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  const id = (value as Record<string, unknown>).id;
  return id !== undefined && id !== null && String(id).trim() !== '';
};

/**
 * Normalizes organization API payloads.
 * Supports direct `{ id, name, ... }`, wrapped `{ result: org }`, and nested wrappers.
 * Does NOT treat Organization.data (metadata) as a response wrapper.
 */
export const extractOrganizationFromResponse = (response: unknown): Organization | null => {
  if (!response || typeof response !== 'object') return null;

  const visit = (value: unknown, depth = 0): Organization | null => {
    if (value == null || typeof value !== 'object' || Array.isArray(value) || depth > 3) return null;

    if (hasOrganizationId(value)) {
      return normalizeOrganizationServiceStatus(value as Organization);
    }

    const record = value as Record<string, unknown>;
    if (record.result !== undefined) {
      return visit(record.result, depth + 1);
    }

    return null;
  };

  return visit(response);
};

export const mergeOrganizationWithPayload = (
  organization: Organization,
  payload: {
    name: string;
    email: string;
    is_premium?: boolean;
    service_status?: OrganizationServiceStatus;
    client_tier?: ClientTier | null;
    legal_entity_type?: LegalEntityType | null;
    has_tax_registration?: boolean | null;
    sector?: string | null;
    accounting_software?: string | null;
    client_brief?: string | null;
    monthly_invoices_count?: string | null;
  },
): Organization => {
  const serviceStatus = payload.service_status
    ?? resolveServiceStatus({
      service_status: organization.service_status,
      is_premium: payload.is_premium ?? organization.is_premium,
    });

  return {
  ...organization,
  name: payload.name || organization.name,
  email: payload.email || organization.email,
  ...buildServiceStatusUpdateFields(serviceStatus),
  client_tier: payload.client_tier !== undefined
    ? payload.client_tier
    : organization.client_tier,
  legal_entity_type: payload.legal_entity_type !== undefined
    ? payload.legal_entity_type
    : organization.legal_entity_type,
  has_tax_registration: payload.has_tax_registration !== undefined
    ? payload.has_tax_registration
    : organization.has_tax_registration,
  sector: payload.sector !== undefined ? payload.sector : organization.sector,
  accounting_software: payload.accounting_software !== undefined
    ? payload.accounting_software
    : organization.accounting_software,
  client_brief: payload.client_brief !== undefined
    ? payload.client_brief
    : organization.client_brief,
  monthly_invoices_count: payload.monthly_invoices_count !== undefined
    ? payload.monthly_invoices_count
    : organization.monthly_invoices_count,
};
};
